interface DataItem {
  [key: string]: any
}

/**
 * Format data for multi-gauge display
 * Transforms data to use standard 'value', 'category', 'percent', and 'full' fields
 */
export const formatDataForMultiGauge = (
  data: DataItem[],
  nbMaxSerie: number,
  typeOfFull: "percent" | "max" | "total",
  valueData: string[],
  groupByData: string | string[],
  objectiveData: string,
  dataNames: string[]
): DataItem[] => {
  let newData = [...data]

  // Case: X valueData | 1 dependsOn (multiple values with objective)
  if (
    Array.isArray(valueData) &&
    valueData.length > 1 &&
    objectiveData &&
    objectiveData.length
  ) {
    newData = valueData.map((valueId, index) => {
      const rawValue = newData[0].hasOwnProperty(valueId) ? newData[0][valueId] : 0
      const objective = newData[0].hasOwnProperty(objectiveData) ? newData[0][objectiveData] : 0
      
      return {
        category: dataNames[index],
        value: rawValue,
        percent: objective > 0 ? (rawValue / objective) * 100 : 0,
        full: 100,
        [objectiveData]: objective,
      }
    })

    return limitData(newData, nbMaxSerie)
  }

  // Case: 1 valueData | no groupBy (add category from dataNames)
  if (!groupByData) {
    newData = newData.map((item) => ({ 
      ...item, 
      category: dataNames[0],
      value: item[valueData[0]] || 0,
    }))
  } else {
    // Add value field from valueData
    newData = newData.map((item) => ({
      ...item,
      value: item[valueData[0]] || 0,
    }))
  }

  // Case: 1 valueData | 1 dependsOn | 1 groupBy
  if (objectiveData && objectiveData.length) {
    newData = newData.map((item) => {
      const rawValue = item[valueData[0]] || 0
      const objective = item[objectiveData] || 0
      
      return {
        ...item,
        value: rawValue,
        percent: objective > 0 ? (rawValue / objective) * 100 : 0,
        full: 100,
      }
    })
    return limitData(newData, nbMaxSerie)
  }

  // Case: 1 valueData | 1 groupBy (no objective)
  if (typeOfFull === "percent") {
    newData = newData.map((item) => ({ ...item, full: 100 }))
  } else if (typeOfFull === "max") {
    const maxValue = getMaxValue(newData, "value")
    newData = newData.map((item) => ({ ...item, full: maxValue }))
  } else if (typeOfFull === "total") {
    const totalValue = getTotalValue(newData, "value")
    newData = newData.map((item) => ({ ...item, full: totalValue }))
  }

  return limitData(newData, nbMaxSerie)
}

/**
 * Return the first nbMaxSerie items in the data
 */
const limitData = (data: DataItem[], nbMaxSerie: number): DataItem[] => {
  if (data && data.length > nbMaxSerie) {
    return data.slice(0, nbMaxSerie)
  }
  return data || []
}

/**
 * Return the maximum value of a key in the data
 */
export const getMaxValue = (data: DataItem[], objectKey: string): number => {
  if (data && data.length && data[0].hasOwnProperty(objectKey)) {
    return Math.max(...data.map((item) => item[objectKey]))
  }
  return 0
}

/**
 * Return the total value of a key in the data
 */
export const getTotalValue = (data: DataItem[], objectKey: string): number => {
  let total = 0
  if (data && data.length && data[0].hasOwnProperty(objectKey)) {
    data.forEach((item) => {
      total += item[objectKey]
    })
    return total
  }
  return 0
}
